import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LandingPage } from "./pages/Landing";
import { SurfaceUnavailable } from "./components/SurfaceUnavailable";
import { currentSession, signOut } from "./lib/auth";
import { currentSurface } from "./lib/host";
import { useSession } from "./lib/operatorSession";
import { PageTracker } from "./lib/analytics";

// Only the public front page is in the first download: the consoles, sign-in and the other
// pages load when they are opened, so the landing paints without the whole staff app.
const named = <K extends string, P extends object>(load: () => Promise<Record<K, ComponentType<P>>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const TicketPage = named(() => import("./pages/TicketPage"), "TicketPage");
const CompanyLinkPage = named(() => import("./pages/CompanyLinkPage"), "CompanyLinkPage");
const PublicBookingPage = named(() => import("./pages/PublicBooking"), "PublicBookingPage");
const TermsPage = named(() => import("./pages/Legal"), "TermsPage");
const OperatorConsole = named(() => import("./OperatorConsole"), "OperatorConsole");
const AdminApp = named(() => import("./AdminApp"), "AdminApp");
const RestaurantApp = named(() => import("./RestaurantApp"), "RestaurantApp");
const DriverApp = named(() => import("./DriverApp"), "DriverApp");
const RestaurantPortal = named(() => import("./pages/RestaurantPortal"), "RestaurantPortal");
const RestaurantLandingPage = named(() => import("./pages/RestaurantLanding"), "RestaurantLandingPage");
const ChoosePasswordPage = named(() => import("./pages/ChoosePassword"), "ChoosePasswordPage");
const SignInPage = named(() => import("./pages/SignIn"), "SignInPage");
const ForgotPasswordPage = named(() => import("./pages/ForgotPassword"), "ForgotPasswordPage");
const CustomerResetPasswordPage = named(() => import("./pages/CustomerResetPassword"), "CustomerResetPasswordPage");

export default function App() {
  return (
    <Suspense fallback={null}>
      <PageTracker />
      <Surface />
    </Suspense>
  );
}

function Surface() {
  const surface = currentSurface();
  // The three staff/partner subdomains get their own small app, each gated to the one
  // principal type that belongs there. Everything else — the bare domain, a dev server, a
  // test with no real hostname — falls through to the app below exactly as it always has:
  // a customer-facing marketing site with an operator sign-in tucked inside it.
  if (surface === "admin") return <AdminApp />;
  if (surface === "restaurant") return <RestaurantApp />;
  if (surface === "driver") return <DriverApp />;

  return <CustomerSite />;
}

function CustomerSite() {
  const operator = useSession();
  const location = useLocation();

  // The static pages (public/caisse/, public/tutoriels/, built by scripts/build-site.mjs) live at
  // their trailing-slash address; without it the server falls back to this app, so send the
  // browser there. nginx redirects first in production; this covers any other server.
  if (/^\/(caisse|tutoriels)(\/[^/.]+)?$/.test(location.pathname)) {
    window.location.replace(`${location.pathname}/${location.search}${location.hash}`);
    return null;
  }

  // The terms of use: public, signed in or not. (The privacy policy is a static page nginx
  // serves at /confidentialite — see pages/Legal.tsx.)
  if (location.pathname === "/conditions") {
    return (
      <Routes>
        <Route path="/conditions" element={<TermsPage />} />
      </Routes>
    );
  }

  // A customer's password reset link (the "Mot de passe oublié" e-mail), signed in or not.
  if (location.pathname === "/mot-de-passe") {
    return (
      <Routes>
        <Route path="/mot-de-passe" element={<CustomerResetPasswordPage />} />
      </Routes>
    );
  }

  // A POS ticket's QR, for anybody who scans it — signed in to the console or not.
  if (location.pathname.startsWith("/t/")) {
    return (
      <Routes>
        <Route path="/t/:code" element={<TicketPage />} />
      </Routes>
    );
  }

  // A delivery company's QR, for anybody who scans it: the company becomes theirs in the app.
  if (location.pathname.startsWith("/d/")) {
    return (
      <Routes>
        <Route path="/d/:code" element={<CompanyLinkPage />} />
      </Routes>
    );
  }

  // A restaurant's booking page, for its customers — signed in or not.
  if (location.pathname.startsWith("/reserver/")) {
    return (
      <Routes>
        <Route path="/reserver/:slug" element={<PublicBookingPage />} />
      </Routes>
    );
  }

  // The public entrance: a welcome page and the two ways in. Kept outside the console shell
  // because none of its navigation means anything to somebody who has not signed in.
  if (!operator) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/restaurant" element={<RestaurantLandingPage />} />
        <Route path="/register" element={<Navigate to="/sign-in" replace />} />
        {/* Customer sign-in is switched off for now — only the admin surface at
            /staff/sign-in is open. See SurfaceUnavailable. */}
        <Route path="/sign-in" element={<SurfaceUnavailable portalName="Customer" />} />
        <Route path="/staff/sign-in" element={<SignInPage />} />
        <Route
          path="/restaurant/sign-in"
          element={<SignInPage surface="restaurant" showBack />}
        />
        {/* A bookmarked restaurant page with no session goes to the restaurant sign-in, not
            the customer welcome page. */}
        <Route path="/restaurant/*" element={<Navigate to="/restaurant/sign-in" replace />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* Anything else sends them to the front door, remembering nothing: a deep link
            into the console is not useful until there is a session to open it with. */}
        <Route
          path="*"
          element={<Navigate to="/" replace state={{ from: location.pathname }} />}
        />
      </Routes>
    );
  }

  // Still on an administrator-issued one-time password: the backend refuses everything else
  // until the account chooses its own, so that comes first.
  const session = currentSession();
  if (session?.mustChangePassword) {
    return (
      <ChoosePasswordPage
        signInPath={session.principalType === "RESTAURANT" ? "/restaurant/sign-in" : "/staff/sign-in"}
      />
    );
  }

  // A signed-in restaurant lives under /restaurant/, its POS dashboard first. Every other
  // address — the welcome page included — sends it to the dashboard, the way the console
  // owns every route for an operator.
  if (session?.principalType === "RESTAURANT") {
    return <RestaurantPortal />;
  }

  // A customer who signs in on this host has an account but no console to see. Telling them
  // so is better than a page of empty panels.
  if (operator && currentSession()?.principalType !== "OPERATOR") {
    return (
      <div className="auth-page">
        <h1>You are signed in</h1>
        <p className="muted">
          This is the 30min staff console. Your account is a customer account,
          so there is nothing here for you yet — the customer app is where you
          order.
        </p>
        <button className="button" onClick={signOut}>
          Sign out
        </button>
      </div>
    );
  }

  return <OperatorConsole operator={operator} />;
}
